import { useQuery } from '@tanstack/react-query';
import { capitalGlobalReferenceDataService } from '@/services';
import type {
  CapitalGlobalReferenceDataItem,
  CapitalGlobalReferenceTableName,
} from '@/services';
import { useDebouncedValue } from '@/shared/utils/use-filters.hook';

type UseCapitalGlobalReferenceDataParams = {
  tableName: CapitalGlobalReferenceTableName;
  search?: string;
  pageSize?: number;
  enabled?: boolean;
};

function formatCnaeLabel(item: CapitalGlobalReferenceDataItem): string {
  return `${item.code} - ${item.description}`.trim();
}

function formatReferenceOption(
  tableName: CapitalGlobalReferenceTableName,
  item: CapitalGlobalReferenceDataItem
): { value: string; label: string } {
  return {
    value: item.code,
    label:
      tableName === 'CNAE'
        ? formatCnaeLabel(item)
        : item.description || item.code,
  };
}

export function useCapitalGlobalReferenceData({
  tableName,
  search = '',
  pageSize = 50,
  enabled = true,
}: UseCapitalGlobalReferenceDataParams) {
  const debouncedSearch = useDebouncedValue(search, 300);

  const query = useQuery({
    queryKey: [
      'capital-global-reference-data',
      tableName,
      debouncedSearch,
      pageSize,
    ],
    queryFn: async () => {
      const result = await capitalGlobalReferenceDataService.getReferenceData({
        tableName,
        filter: debouncedSearch || undefined,
        pageNumber: 1,
        pageSize,
      });

      if (!result.success || !result.data) {
        throw new Error(result.error ?? 'Falha ao buscar dados de referência');
      }

      return result.data.data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  return {
    ...query,
    options: (query.data ?? []).map(item =>
      formatReferenceOption(tableName, item)
    ),
  };
}
